import { useCallback, useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import {
  acceptFriendRequest,
  cancelFriendRequest,
  createFriendRequest,
  declineFriendRequest,
  findFriendTarget,
  listFriends,
  listIncomingFriendRequests,
  listOutgoingFriendRequests,
} from '../services/friendService';
import { ensureUserProfile } from '../services/profileService';

function Friends() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [friendInput, setFriendInput] = useState('');
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadFriends = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const loadedProfile = await ensureUserProfile(currentUser);
      const [loadedIncoming, loadedOutgoing, loadedFriends] = await Promise.all([
        listIncomingFriendRequests(currentUser.uid),
        listOutgoingFriendRequests(currentUser.uid),
        listFriends(currentUser.uid),
      ]);
      setProfile(loadedProfile);
      setIncoming(loadedIncoming);
      setOutgoing(loadedOutgoing);
      setFriends(loadedFriends);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  async function handleSendRequest(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const target = await findFriendTarget(friendInput);
      if (!target) {
        throw new Error('No user found for that username or email.');
      }
      await createFriendRequest(profile, target);
      setFriendInput('');
      setMessage('Friend request sent.');
      await loadFriends();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAccept(request) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await acceptFriendRequest(request);
      setMessage('Friend request accepted.');
      await loadFriends();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDecline(requestId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await declineFriendRequest(requestId);
      setMessage('Friend request declined.');
      await loadFriends();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel(requestId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await cancelFriendRequest(requestId);
      setMessage('Friend request cancelled.');
      await loadFriends();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <SEO title="Friends" />
      <section className="page-heading">
        <p className="eyebrow">Trusted audience</p>
        <h1>Manage who can see friends-only profile components.</h1>
        <p>Send requests by username or exact email. Accepted friends can see content marked friends.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="editor-layout">
        <form className="surface-card stacked-form" onSubmit={handleSendRequest}>
          <h2>Add a friend</h2>
          <label>Username or email<input value={friendInput} onChange={(event) => setFriendInput(event.target.value)} /></label>
          <button className="button" type="submit" disabled={saving || !friendInput.trim()}>{saving ? 'Working...' : 'Send request'}</button>
        </form>
        <div className="surface-card">
          <h2>Friends</h2>
          {loading ? <p>Loading friends...</p> : null}
          {!loading && friends.length === 0 ? <p className="muted">No accepted friends yet.</p> : null}
          <div className="item-list">
            {friends.map((friend) => <article className="content-item" key={friend.id}><h3>{friend.friendUid}</h3></article>)}
          </div>
        </div>
      </section>
      <section className="editor-layout">
        <div className="surface-card">
          <h2>Incoming requests</h2>
          {!loading && incoming.length === 0 ? <p className="muted">No incoming requests.</p> : null}
          <div className="item-list">
            {incoming.map((request) => (
              <article className="content-item" key={request.id}>
                <h3>{request.fromDisplayName || request.fromUsername || request.fromUid}</h3>
                <p className="muted">@{request.fromUsername || request.fromUid}</p>
                <div className="button-row compact-row">
                  <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleAccept(request)}>Accept</button>
                  <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleDecline(request.id)}>Decline</button>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="surface-card">
          <h2>Sent requests</h2>
          {!loading && outgoing.length === 0 ? <p className="muted">No pending sent requests.</p> : null}
          <div className="item-list">
            {outgoing.map((request) => (
              <article className="content-item" key={request.id}>
                <h3>{request.toDisplayName || request.toUsername || request.toUid}</h3>
                <p className="muted">@{request.toUsername || request.toUid}</p>
                <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleCancel(request.id)}>Cancel</button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Friends;
